import React, { ChangeEvent, useEffect, useState } from 'react';
import { ResizableInput } from 'shared/components/ui/resizable-input';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { isNumber } from 'shared/utils/is-number';
import { parseDateTime } from 'shared/utils/parse-date-time';
import { parseTimeToMsOfDay } from 'shared/utils/parse-time-to-ms-of-day';
import { processStringToHoursAndMinutes } from 'shared/utils/process-string-to-hours-and-minutes';

export type TimePickerInputProps = Omit<
  React.ComponentProps<typeof ResizableInput>,
  'onChange' | 'value'
> & {
  onChange: (value?: number) => void;
  timeFormat: TimeFormatOptions;
  value?: number | null;
  icon?: React.FC;
};

export const TimePickerInput: React.FC<TimePickerInputProps> = ({
  onChange,
  value,
  timeFormat,
  onBlur,
  ...rest
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const displayValue = isNumber(value) ? parseDateTime(value, timeFormat) : '';

  const handleInputChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    if (!target.value.length) {
      // if empty, the user removed the time, so it doesn't want a Date
      onChange();
      setInputValue('');
      return;
    }

    // Process textual input to the format we expect
    const hoursAndMinutes = processStringToHoursAndMinutes(
      target.value,
      TimeFormatOptions.Military,
    );

    // The value was normalised to 24h above, so it must be read back as 24h too —
    // parsing it with the user's format yields an invalid date for 12h users.
    const msOfDay = parseTimeToMsOfDay(
      hoursAndMinutes,
      TimeFormatOptions.Military,
    );

    if (msOfDay !== null) {
      onChange(msOfDay);
      return;
    }

    // Update the input if it isn't a valid time format yet
    setInputValue(hoursAndMinutes);
  };

  // a consumer may refuse an emptied or unparsable entry (a mandatory time); snap the text
  // back to what `value` actually holds so the field never keeps showing a phantom edit
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setInputValue(displayValue);
    onBlur?.(event);
  };

  useEffect(() => {
    setInputValue(displayValue);
  }, [displayValue]);

  return (
    <ResizableInput
      value={inputValue}
      onChange={handleInputChange}
      onBlur={handleBlur}
      {...rest}
    />
  );
};
